/**
 * Stockage local chiffré des paquets et deltas terrain (OPS-013, SEC-004).
 *
 * Les charges utiles sont chiffrées en AES-GCM avec une clé WebCrypto non extractible
 * propre à la session terrain. CacheStorage ne contient jamais de données métier.
 */
import type {
  FieldMission,
  MissionPackage,
  MissionStatus,
} from "@/api/types";

const DATABASE_NAME = "infranet-terrain";
const DATABASE_VERSION = 2;
const MISSIONS_STORE = "missions";
const DELTAS_STORE = "deltas";
const KEYS_STORE = "encryption-keys";
const VALID_MISSION_STATUSES = new Set<MissionStatus>([
  "planned",
  "in_progress",
  "completed",
  "cancelled",
]);

export interface StoredMission {
  storageKey: string;
  scope: string;
  /** UUID V2. Les paquets déjà stockés sous un entier v1 sont relus par clé, pas par type. */
  missionId: string;
  code: string;
  name: string;
  status: MissionStatus;
  deadline: string;
  packageVersion: number;
  generatedAt: string;
  expiresAt: string;
  downloadedAt: string;
  sourceLastUpdated: string;
  payload: MissionPackage;
}

export interface MissionDelta {
  id: string;
  revision: string;
  scope: string;
  missionId: string;
  kind: "mission_status";
  body: { status: MissionStatus };
  basePackageVersion: number;
  baseLastUpdated: string;
  createdAt: string;
  attempts: number;
  lastError: string | null;
}

interface EncryptedMissionRecord {
  storageKey: string;
  revision: string;
  scope: string;
  missionId: string;
  expiresAt: string;
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
}

interface EncryptedDeltaRecord {
  id: string;
  revision: string;
  scope: string;
  missionId: string;
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
}

interface EncryptionKeyRecord {
  scope: string;
  key: CryptoKey;
}

function missionStorageKey(scope: string, missionId: string): string {
  return encodeURIComponent(scope) + ":mission:" + String(missionId);
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Lecture IndexedDB impossible."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Transaction IndexedDB impossible."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Transaction IndexedDB annulée."));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (!("indexedDB" in globalThis) || !crypto.subtle) {
    return Promise.reject(
      new Error("Le stockage chiffré n'est pas disponible sur ce navigateur."),
    );
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onerror = () =>
      reject(request.error ?? new Error("Ouverture IndexedDB impossible."));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const database = request.result;
      if (!database.objectStoreNames.contains(MISSIONS_STORE)) {
        const missions = database.createObjectStore(MISSIONS_STORE, {
          keyPath: "storageKey",
        });
        missions.createIndex("scope", "scope", { unique: false });
      }
      if (!database.objectStoreNames.contains(DELTAS_STORE)) {
        const deltas = database.createObjectStore(DELTAS_STORE, { keyPath: "id" });
        deltas.createIndex("scope", "scope", { unique: false });
      }
      if (!database.objectStoreNames.contains(KEYS_STORE)) {
        database.createObjectStore(KEYS_STORE, { keyPath: "scope" });
      }

      // La v1 n'a jamais été publiée : ses brouillons en clair sont invalidés.
      if (event.oldVersion > 0 && event.oldVersion < 2 && request.transaction) {
        request.transaction.objectStore(MISSIONS_STORE).clear();
        request.transaction.objectStore(DELTAS_STORE).clear();
      }
    };
  });
}

async function readEncryptionKey(
  database: IDBDatabase,
  scope: string,
): Promise<CryptoKey | null> {
  const transaction = database.transaction(KEYS_STORE, "readonly");
  const done = transactionDone(transaction);
  const record = (await requestResult(
    transaction.objectStore(KEYS_STORE).get(scope),
  )) as EncryptionKeyRecord | undefined;
  await done;
  return record?.key ?? null;
}

async function encryptionKey(
  database: IDBDatabase,
  scope: string,
): Promise<CryptoKey> {
  const existing = await readEncryptionKey(database, scope);
  if (existing) {
    return existing;
  }

  const generated = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  const transaction = database.transaction(KEYS_STORE, "readwrite");
  const done = transactionDone(transaction);
  const store = transaction.objectStore(KEYS_STORE);
  const raced = (await requestResult(store.get(scope))) as
    | EncryptionKeyRecord
    | undefined;
  if (!raced) {
    store.put({ scope, key: generated } satisfies EncryptionKeyRecord);
  }
  await done;
  return raced?.key ?? generated;
}

function bytesBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function additionalData(
  kind: "mission" | "delta",
  primaryKey: string,
  binding: string,
): ArrayBuffer {
  return bytesBuffer(
    new TextEncoder().encode("infranet:" + kind + ":" + primaryKey + ":" + binding),
  );
}

async function encryptPayload<T>(
  key: CryptoKey,
  kind: "mission" | "delta",
  primaryKey: string,
  binding: string,
  value: T,
): Promise<{ iv: ArrayBuffer; ciphertext: ArrayBuffer }> {
  const iv = bytesBuffer(crypto.getRandomValues(new Uint8Array(12)));
  const plaintext = bytesBuffer(new TextEncoder().encode(JSON.stringify(value)));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: additionalData(kind, primaryKey, binding),
    },
    key,
    plaintext,
  );
  return { iv, ciphertext };
}

async function decryptPayload<T>(
  key: CryptoKey,
  kind: "mission" | "delta",
  primaryKey: string,
  binding: string,
  iv: ArrayBuffer,
  ciphertext: ArrayBuffer,
): Promise<T> {
  const plaintext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: additionalData(kind, primaryKey, binding),
    },
    key,
    ciphertext,
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

async function encodeMission(
  key: CryptoKey,
  mission: StoredMission,
  revision = crypto.randomUUID(),
): Promise<EncryptedMissionRecord> {
  const binding = [
    mission.scope,
    String(mission.missionId),
    mission.expiresAt,
    revision,
  ].join("|");
  const encrypted = await encryptPayload(
    key,
    "mission",
    mission.storageKey,
    binding,
    mission,
  );
  return {
    storageKey: mission.storageKey,
    revision,
    scope: mission.scope,
    missionId: mission.missionId,
    expiresAt: mission.expiresAt,
    ...encrypted,
  };
}

function decodeMission(
  key: CryptoKey,
  record: EncryptedMissionRecord,
): Promise<StoredMission> {
  const binding = [
    record.scope,
    String(record.missionId),
    record.expiresAt,
    record.revision,
  ].join("|");
  return decryptPayload<StoredMission>(
    key,
    "mission",
    record.storageKey,
    binding,
    record.iv,
    record.ciphertext,
  ).then((mission) => {
    if (
      mission.storageKey !== record.storageKey ||
      mission.scope !== record.scope ||
      mission.missionId !== record.missionId ||
      mission.expiresAt !== record.expiresAt
    ) {
      throw new Error("Métadonnées du paquet local incohérentes.");
    }
    return mission;
  });
}

async function encodeDelta(
  key: CryptoKey,
  delta: MissionDelta,
): Promise<EncryptedDeltaRecord> {
  const binding = [
    delta.scope,
    String(delta.missionId),
    delta.revision,
  ].join("|");
  const encrypted = await encryptPayload(
    key,
    "delta",
    delta.id,
    binding,
    delta,
  );
  return {
    id: delta.id,
    revision: delta.revision,
    scope: delta.scope,
    missionId: delta.missionId,
    ...encrypted,
  };
}

function decodeDelta(
  key: CryptoKey,
  record: EncryptedDeltaRecord,
): Promise<MissionDelta> {
  const binding = [
    record.scope,
    String(record.missionId),
    record.revision,
  ].join("|");
  return decryptPayload<MissionDelta>(
    key,
    "delta",
    record.id,
    binding,
    record.iv,
    record.ciphertext,
  ).then((delta) => {
    if (
      delta.id !== record.id ||
      delta.scope !== record.scope ||
      delta.missionId !== record.missionId ||
      delta.revision !== record.revision
    ) {
      throw new Error("Métadonnées du delta local incohérentes.");
    }
    return delta;
  });
}

export async function purgeOfflineScope(scope: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(
      [MISSIONS_STORE, DELTAS_STORE, KEYS_STORE],
      "readwrite",
    );
    const done = transactionDone(transaction);
    for (const storeName of [MISSIONS_STORE, DELTAS_STORE]) {
      const store = transaction.objectStore(storeName);
      const keys = await requestResult(store.index("scope").getAllKeys(scope));
      keys.forEach((key) => store.delete(key));
    }
    transaction.objectStore(KEYS_STORE).delete(scope);
    await done;
  } finally {
    database.close();
  }
}

export async function saveMissionPackage(
  scope: string,
  mission: FieldMission,
  payload: MissionPackage,
): Promise<StoredMission> {
  if (payload.mission.id !== mission.id) {
    throw new Error("Le paquet reçu ne correspond pas à la mission demandée.");
  }
  if (Date.parse(payload.package.expires_at) <= Date.now()) {
    throw new Error("Le paquet reçu est déjà expiré.");
  }

  const stored: StoredMission = {
    storageKey: missionStorageKey(scope, mission.id),
    scope,
    missionId: mission.id,
    code: mission.business_code,
    name: mission.name,
    status: mission.status,
    deadline: mission.due_at ?? "",
    packageVersion: payload.package.version,
    generatedAt: payload.package.generated_at,
    expiresAt: payload.package.expires_at,
    downloadedAt: new Date().toISOString(),
    // La V2 n'expose pas `last_updated` : l'horodatage de dernière écriture s'appelle
    // `updated_at`, et c'est lui qui sert de base à la détection de conflit.
    sourceLastUpdated: mission.updated_at,
    payload,
  };

  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, scope);
    const encrypted = await encodeMission(key, stored);
    const transaction = database.transaction(MISSIONS_STORE, "readwrite");
    const done = transactionDone(transaction);
    transaction.objectStore(MISSIONS_STORE).put(encrypted);
    await done;
    return stored;
  } finally {
    database.close();
  }
}

export async function listStoredMissions(scope: string): Promise<StoredMission[]> {
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, scope);
    const readTransaction = database.transaction(MISSIONS_STORE, "readonly");
    const readDone = transactionDone(readTransaction);
    const records = (await requestResult(
      readTransaction.objectStore(MISSIONS_STORE).index("scope").getAll(scope),
    )) as EncryptedMissionRecord[];
    await readDone;

    const now = Date.now();
    const validRecords = records.filter((record) => Date.parse(record.expiresAt) > now);
    const expiredRecords = records.filter((record) => Date.parse(record.expiresAt) <= now);

    if (expiredRecords.length > 0) {
      const expiredMissionIds = new Set(expiredRecords.map((record) => record.missionId));
      const transaction = database.transaction(
        [MISSIONS_STORE, DELTAS_STORE],
        "readwrite",
      );
      const done = transactionDone(transaction);
      const missionStore = transaction.objectStore(MISSIONS_STORE);
      expiredRecords.forEach((record) => missionStore.delete(record.storageKey));
      const deltaStore = transaction.objectStore(DELTAS_STORE);
      const deltas = (await requestResult(
        deltaStore.index("scope").getAll(scope),
      )) as EncryptedDeltaRecord[];
      deltas
        .filter((delta) => expiredMissionIds.has(delta.missionId))
        .forEach((delta) => deltaStore.delete(delta.id));
      await done;
    }

    const missions = await Promise.all(
      validRecords.map((record) => decodeMission(key, record)),
    );
    return missions.sort((left, right) => left.deadline.localeCompare(right.deadline));
  } finally {
    database.close();
  }
}

export async function listMissionDeltas(scope: string): Promise<MissionDelta[]> {
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, scope);
    const transaction = database.transaction(DELTAS_STORE, "readonly");
    const done = transactionDone(transaction);
    const records = (await requestResult(
      transaction.objectStore(DELTAS_STORE).index("scope").getAll(scope),
    )) as EncryptedDeltaRecord[];
    await done;
    const values = await Promise.all(records.map((record) => decodeDelta(key, record)));
    return values.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  } finally {
    database.close();
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function queueMissionStatusDelta(
  scope: string,
  missionId: string,
  status: MissionStatus,
): Promise<void> {
  // La V2 numérote en UUID. L'ancien contrôle exigeait un entier positif : appliqué à un
  // identifiant V2, il refusait TOUS les changements d'état terrain, et le message parlait
  // d'un « delta invalide » sans dire que c'était l'identifiant qui ne convenait pas.
  if (!UUID.test(missionId) || !VALID_MISSION_STATUSES.has(status)) {
    throw new Error("Delta de mission invalide : identifiant ou état hors contrat V2.");
  }

  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, scope);
    const storageKey = missionStorageKey(scope, missionId);
    const readTransaction = database.transaction(MISSIONS_STORE, "readonly");
    const readDone = transactionDone(readTransaction);
    const currentRecord = (await requestResult(
      readTransaction.objectStore(MISSIONS_STORE).get(storageKey),
    )) as EncryptedMissionRecord | undefined;
    await readDone;
    if (!currentRecord || currentRecord.scope !== scope) {
      throw new Error("Le paquet de mission n'est pas disponible hors connexion.");
    }

    const mission = await decodeMission(key, currentRecord);
    mission.status = status;
    const delta: MissionDelta = {
      id: storageKey + ":status",
      revision: crypto.randomUUID(),
      scope,
      missionId,
      kind: "mission_status",
      body: { status },
      basePackageVersion: mission.packageVersion,
      baseLastUpdated: mission.sourceLastUpdated,
      createdAt: new Date().toISOString(),
      attempts: 0,
      lastError: null,
    };
    const [encryptedMission, encryptedDelta] = await Promise.all([
      encodeMission(key, mission),
      encodeDelta(key, delta),
    ]);

    const transaction = database.transaction(
      [MISSIONS_STORE, DELTAS_STORE],
      "readwrite",
    );
    const done = transactionDone(transaction);
    const missions = transaction.objectStore(MISSIONS_STORE);
    const latest = (await requestResult(missions.get(storageKey))) as
      | EncryptedMissionRecord
      | undefined;
    if (latest?.revision !== currentRecord.revision) {
      transaction.abort();
      throw new Error("La mission locale a changé ; recommencez la modification.");
    }
    missions.put(encryptedMission);
    transaction.objectStore(DELTAS_STORE).put(encryptedDelta);
    await done;
  } finally {
    database.close();
  }
}

export async function deleteMissionDeltaIfUnchanged(
  delta: MissionDelta,
): Promise<boolean> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(DELTAS_STORE, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(DELTAS_STORE);
    const current = (await requestResult(store.get(delta.id))) as
      | EncryptedDeltaRecord
      | undefined;
    const unchanged = current?.revision === delta.revision;
    if (unchanged) {
      store.delete(delta.id);
    }
    await done;
    return unchanged;
  } finally {
    database.close();
  }
}

async function compareAndPutDelta(
  database: IDBDatabase,
  expectedRevision: string,
  record: EncryptedDeltaRecord,
): Promise<boolean> {
  const transaction = database.transaction(DELTAS_STORE, "readwrite");
  const done = transactionDone(transaction);
  const store = transaction.objectStore(DELTAS_STORE);
  const current = (await requestResult(store.get(record.id))) as
    | EncryptedDeltaRecord
    | undefined;
  const unchanged = current?.revision === expectedRevision;
  if (unchanged) {
    store.put(record);
  }
  await done;
  return unchanged;
}

export async function rebaseNewerMissionDelta(
  sentDelta: MissionDelta,
  serverLastUpdated: string,
): Promise<void> {
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, sentDelta.scope);
    const transaction = database.transaction(DELTAS_STORE, "readonly");
    const done = transactionDone(transaction);
    const record = (await requestResult(
      transaction.objectStore(DELTAS_STORE).get(sentDelta.id),
    )) as EncryptedDeltaRecord | undefined;
    await done;
    if (!record || record.revision === sentDelta.revision) {
      return;
    }
    const current = await decodeDelta(key, record);
    if (current.baseLastUpdated === sentDelta.baseLastUpdated) {
      current.baseLastUpdated = serverLastUpdated;
      await compareAndPutDelta(
        database,
        record.revision,
        await encodeDelta(key, current),
      );
    }
  } finally {
    database.close();
  }
}

export async function updateStoredMissionAfterSync(
  sentDelta: MissionDelta,
  serverMission: FieldMission,
): Promise<void> {
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, sentDelta.scope);
    const storageKey = missionStorageKey(sentDelta.scope, sentDelta.missionId);
    const readTransaction = database.transaction(MISSIONS_STORE, "readonly");
    const readDone = transactionDone(readTransaction);
    const record = (await requestResult(
      readTransaction.objectStore(MISSIONS_STORE).get(storageKey),
    )) as EncryptedMissionRecord | undefined;
    await readDone;
    if (!record) {
      return;
    }
    const mission = await decodeMission(key, record);
    if (mission.sourceLastUpdated !== sentDelta.baseLastUpdated) {
      return;
    }
    mission.sourceLastUpdated = serverMission.updated_at;
    if (mission.status === sentDelta.body.status) {
      mission.status = serverMission.status;
    }
    const updated = await encodeMission(key, mission);

    const transaction = database.transaction(MISSIONS_STORE, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(MISSIONS_STORE);
    const current = (await requestResult(store.get(storageKey))) as
      | EncryptedMissionRecord
      | undefined;
    if (current?.revision === record.revision) {
      store.put(updated);
    }
    await done;
  } finally {
    database.close();
  }
}

export async function markMissionDeltaFailed(
  delta: MissionDelta,
  message: string,
): Promise<void> {
  const database = await openDatabase();
  try {
    const key = await encryptionKey(database, delta.scope);
    const transaction = database.transaction(DELTAS_STORE, "readonly");
    const done = transactionDone(transaction);
    const record = (await requestResult(
      transaction.objectStore(DELTAS_STORE).get(delta.id),
    )) as EncryptedDeltaRecord | undefined;
    await done;
    if (!record || record.revision !== delta.revision) {
      return;
    }
    const current = await decodeDelta(key, record);
    current.attempts += 1;
    current.lastError = message.slice(0, 500);
    await compareAndPutDelta(
      database,
      record.revision,
      await encodeDelta(key, current),
    );
  } finally {
    database.close();
  }
}
